/**
 * Comprehensive Export Utilities
 * For Users and Admins - ZO Bull Platform
 * 
 * Supports: CSV, XLS, JSON, PDF formats
 * AI-Ready structured data exports
 */

import * as XLSX from 'xlsx';

// ============================================================================
// BASE EXPORT FUNCTIONS
// ============================================================================

export const convertToCSV = (data: any[]): string => {
  if (!data?.length) return '';
  const headers = Object.keys(data[0]);
  const rows = [
    headers.join(','),
    ...data.map(row => headers.map(h => {
      const val = row[h];
      if (typeof val === 'string' && (val.includes(',') || val.includes('"') || val.includes('\n'))) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    }).join(','))
  ];
  return rows.join('\n');
};

export const downloadCSV = (data: any[], filename: string) => {
  const blob = new Blob([convertToCSV(data)], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
};

export const downloadXLS = (data: any[], filename: string, sheets: Record<string, any[]> = {}) => {
  const workbook = XLSX.utils.book_new();
  
  // Default sheet
  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Additional sheets
  Object.entries(sheets).forEach(([name, sheetData]) => {
    const ws = XLSX.utils.json_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(workbook, ws, name);
  });
  
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const downloadJSON = (data: any, filename: string, pretty = true) => {
  const json = pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
  downloadBlob(blob, `${filename}.json`);
};

export const downloadPDF = (htmlContent: string, filename: string) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${filename}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        table { border-collapse: collapse; width: 100%; margin: 20px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #4a5568; color: white; }
        tr:nth-child(even) { background: #f2f2f2; }
        h1, h2 { color: #1a202c; }
        .header { background: #4a5568; color: white; padding: 20px; margin-bottom: 20px; }
        .meta { color: #666; font-size: 12px; margin-top: 20px; }
      </style>
    </head>
    <body>
      ${htmlContent}
      <div class="meta">Generated: ${new Date().toLocaleString()}</div>
      <script>window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 500); }; <\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================================================
// USER EXPORTS
// ============================================================================

export const userExports = {
  /**
   * Export User Activity Logs
   */
  activityLogs: (logs: any[]) => {
    const formattedData = logs.map(log => ({
      timestamp: log.timestamp || log.created_at,
      action: log.action,
      type: log.type,
      details: log.details || log.description,
      ip_address: log.ip_address,
      device: log.device,
      status: log.status
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'user_activity_logs'),
      xls: () => downloadXLS(formattedData, 'user_activity_logs'),
      json: () => downloadJSON({
        type: "user_activity_logs",
        exported_at: new Date().toISOString(),
        total_records: logs.length,
        data: formattedData
      }, 'user_activity_logs'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>User Activity Logs</h1></div>
        <p>Total Records: ${logs.length}</p>
        <table>
          <tr><th>Timestamp</th><th>Action</th><th>Details</th><th>Status</th></tr>
          ${formattedData.slice(0, 100).map(l => `
            <tr>
              <td>${l.timestamp}</td>
              <td>${l.action}</td>
              <td>${l.details}</td>
              <td>${l.status}</td>
            </tr>
          `).join('')}
        </table>
      `, 'user_activity_logs'),
      ai: () => downloadJSON({
        type: "user_activity_logs",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_actions: logs.length,
          action_types: [...new Set(logs.map(l => l.action))],
          date_range: {
            start: logs[0]?.timestamp,
            end: logs[logs.length - 1]?.timestamp
          }
        },
        data: formattedData
      }, 'ai_user_activity_logs')
    };
  },

  /**
   * Export Trade History
   */
  tradeHistory: (trades: any[]) => {
    const formattedData = trades.map(t => ({
      trade_id: t.id || t.trade_id,
      date: t.date || t.timestamp,
      strategy: t.strategy_name || t.strategy,
      symbol: t.symbol,
      exchange: t.exchange,
      type: t.type || t.trade_type,
      side: t.side,
      quantity: t.quantity,
      entry_price: t.entry_price,
      exit_price: t.exit_price,
      pnl: t.pnl,
      pnl_percentage: t.pnl_percentage,
      broker: t.broker,
      status: t.status
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'trade_history'),
      xls: () => downloadXLS(formattedData, 'trade_history'),
      json: () => downloadJSON({
        type: "trade_history",
        exported_at: new Date().toISOString(),
        total_trades: trades.length,
        data: formattedData
      }, 'trade_history'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Trade History</h1></div>
        <p>Total Trades: ${trades.length}</p>
        <table>
          <tr><th>Date</th><th>Strategy</th><th>Symbol</th><th>Side</th><th>P&L</th><th>Status</th></tr>
          ${formattedData.slice(0, 50).map(t => `
            <tr>
              <td>${t.date}</td>
              <td>${t.strategy}</td>
              <td>${t.symbol}</td>
              <td>${t.side}</td>
              <td style="color: ${t.pnl >= 0 ? 'green' : 'red'}">₹${t.pnl}</td>
              <td>${t.status}</td>
            </tr>
          `).join('')}
        </table>
      `, 'trade_history'),
      ai: () => downloadJSON({
        type: "trade_history",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_trades: trades.length,
          profitable_trades: trades.filter(t => t.pnl > 0).length,
          losing_trades: trades.filter(t => t.pnl < 0).length,
          total_pnl: trades.reduce((a, t) => a + (t.pnl || 0), 0),
          win_rate: (trades.filter(t => t.pnl > 0).length / trades.length * 100).toFixed(2) + '%'
        },
        data: formattedData
      }, 'ai_trade_history')
    };
  },

  /**
   * Export Subscription & Billing
   */
  subscriptions: (data: any) => {
    const { subscription, invoices, transactions } = data;
    
    return {
      csv: () => {
        if (invoices?.length) downloadCSV(invoices, 'subscription_invoices');
        else if (transactions?.length) downloadCSV(transactions, 'subscription_transactions');
      },
      xls: () => downloadXLS(invoices || transactions || [], 'subscription_data', {
        'Invoices': invoices || [],
        'Transactions': transactions || [],
        'Subscription': subscription ? [subscription] : []
      }),
      json: () => downloadJSON({
        type: "subscription_data",
        exported_at: new Date().toISOString(),
        subscription: subscription,
        invoices: invoices,
        transactions: transactions
      }, 'subscription_data'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Subscription & Billing</h1></div>
        ${subscription ? `
          <h2>Current Plan</h2>
          <p><strong>Plan:</strong> ${subscription.plan_name}</p>
          <p><strong>Status:</strong> ${subscription.status}</p>
          <p><strong>Amount:</strong> ₹${subscription.amount}</p>
        ` : ''}
        ${invoices?.length ? `
          <h2>Invoices</h2>
          <table>
            <tr><th>Date</th><th>Amount</th><th>Status</th></tr>
            ${invoices.map(i => `
              <tr><td>${i.date}</td><td>₹${i.amount}</td><td>${i.status}</td></tr>
            `).join('')}
          </table>
        ` : ''}
      `, 'subscription_data'),
      ai: () => downloadJSON({
        type: "subscription_data",
        version: "1.0",
        exported_at: new Date().toISOString(),
        subscription: subscription,
        summary: {
          plan: subscription?.plan_name,
          status: subscription?.status,
          total_invoices: invoices?.length || 0,
          total_transactions: transactions?.length || 0,
          total_paid: invoices?.filter(i => i.status === 'paid').reduce((a, i) => a + i.amount, 0) || 0
        },
        invoices: invoices,
        transactions: transactions
      }, 'ai_subscription_data')
    };
  },

  /**
   * Export Exchange Connections
   */
  exchangeConnections: (connections: any[]) => {
    const formattedData = connections.map(c => ({
      exchange: c.exchange_name || c.exchange,
      api_key: c.api_key?.slice(0, 8) + '...' || 'Connected',
      status: c.status,
      permissions: c.permissions?.join(', ') || 'N/A',
      connected_at: c.connected_at,
      last_sync: c.last_sync
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'exchange_connections'),
      xls: () => downloadXLS(formattedData, 'exchange_connections'),
      json: () => downloadJSON({
        type: "exchange_connections",
        exported_at: new Date().toISOString(),
        data: formattedData
      }, 'exchange_connections'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Exchange Connections</h1></div>
        <table>
          <tr><th>Exchange</th><th>Status</th><th>Permissions</th><th>Connected</th></tr>
          ${formattedData.map(c => `
            <tr><td>${c.exchange}</td><td>${c.status}</td><td>${c.permissions}</td><td>${c.connected_at}</td></tr>
          `).join('')}
        </table>
      `, 'exchange_connections'),
      ai: () => downloadJSON({
        type: "exchange_connections",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_connections: connections.length,
          active_connections: connections.filter(c => c.status === 'active').length
        },
        data: formattedData
      }, 'ai_exchange_connections')
    };
  },

  /**
   * Export Notification Settings
   */
  notificationSettings: (settings: any) => {
    return {
      csv: () => {
        const data = Object.entries(settings.channels || {}).map(([ch, enabled]) => ({
          channel: ch,
          enabled: enabled ? 'Yes' : 'No'
        }));
        downloadCSV(data, 'notification_settings');
      },
      xls: () => downloadXLS(
        Object.entries(settings).map(([key, value]) => ({ setting: key, value: typeof value === 'object' ? JSON.stringify(value) : value })),
        'notification_settings'
      ),
      json: () => downloadJSON({
        type: "notification_settings",
        exported_at: new Date().toISOString(),
        data: settings
      }, 'notification_settings'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Notification Settings</h1></div>
        <pre>${JSON.stringify(settings, null, 2)}</pre>
      `, 'notification_settings'),
      ai: () => downloadJSON({
        type: "notification_settings",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_channels: Object.keys(settings.channels || {}).length,
          enabled_channels: Object.values(settings.channels || {}).filter(Boolean).length
        },
        data: settings
      }, 'ai_notification_settings')
    };
  },

  /**
   * Export Broker Settings
   */
  brokerSettings: (brokers: any[]) => {
    const formattedData = brokers.map(b => ({
      broker_name: b.broker_name || b.name,
      api_key: b.api_key ? b.api_key.slice(0, 8) + '...' : 'N/A',
      api_secret: b.api_secret ? '***' : 'N/A',
      status: b.status,
      permissions: b.permissions?.join(', ') || 'N/A',
      connected_at: b.connected_at,
      last_trade: b.last_trade,
      total_trades: b.total_trades,
      is_active: b.is_active ? 'Yes' : 'No'
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'broker_settings'),
      xls: () => downloadXLS(formattedData, 'broker_settings'),
      json: () => downloadJSON({
        type: "broker_settings",
        exported_at: new Date().toISOString(),
        total_brokers: brokers.length,
        data: formattedData
      }, 'broker_settings'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Broker Settings</h1></div>
        <p>Total Brokers: ${brokers.length}</p>
        <table>
          <tr><th>Broker</th><th>Status</th><th>Total Trades</th><th>Connected</th></tr>
          ${formattedData.map(b => `
            <tr><td>${b.broker_name}</td><td>${b.status}</td><td>${b.total_trades}</td><td>${b.connected_at}</td></tr>
          `).join('')}
        </table>
      `, 'broker_settings'),
      ai: () => downloadJSON({
        type: "broker_settings",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_brokers: brokers.length,
          active_brokers: brokers.filter(b => b.is_active).length,
          total_trades: brokers.reduce((a, b) => a + (b.total_trades || 0), 0)
        },
        data: formattedData
      }, 'ai_broker_settings')
    };
  },

  /**
   * Export Notifications History
   */
  notificationsHistory: (notifications: any[]) => {
    const formattedData = notifications.map(n => ({
      id: n.id,
      timestamp: n.timestamp || n.created_at,
      channel: n.channel,
      type: n.type,
      title: n.title,
      message: n.message,
      status: n.status,
      sent_at: n.sent_at,
      delivered_at: n.delivered_at,
      read_at: n.read_at,
      error: n.error || 'None'
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'notifications_history'),
      xls: () => downloadXLS(formattedData, 'notifications_history'),
      json: () => downloadJSON({
        type: "notifications_history",
        exported_at: new Date().toISOString(),
        total_notifications: notifications.length,
        data: formattedData
      }, 'notifications_history'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Notifications History</h1></div>
        <p>Total: ${notifications.length}</p>
        <table>
          <tr><th>Time</th><th>Channel</th><th>Title</th><th>Status</th></tr>
          ${formattedData.slice(0, 50).map(n => `
            <tr><td>${n.timestamp}</td><td>${n.channel}</td><td>${n.title}</td><td>${n.status}</td></tr>
          `).join('')}
        </table>
      `, 'notifications_history'),
      ai: () => downloadJSON({
        type: "notifications_history",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total: notifications.length,
          sent: notifications.filter(n => n.status === 'sent' || n.status === 'delivered').length,
          failed: notifications.filter(n => n.status === 'failed').length,
          pending: notifications.filter(n => n.status === 'pending').length,
          by_channel: notifications.reduce((acc, n) => {
            acc[n.channel] = (acc[n.channel] || 0) + 1;
            return acc;
          }, {})
        },
        data: formattedData
      }, 'ai_notifications_history')
    };
  },

  /**
   * Export Channel Notifications (success/failure)
   */
  channelNotifications: (data: any) => {
    const { successful, failed, pending } = data;
    
    const formatNotifications = (notifications: any[]) => notifications?.map(n => ({
      id: n.id,
      timestamp: n.timestamp || n.created_at,
      channel: n.channel,
      recipient: n.recipient,
      title: n.title,
      message: n.message,
      status: n.status,
      sent_at: n.sent_at,
      error: n.error || 'N/A',
      retry_count: n.retry_count || 0
    })) || [];
    
    return {
      csv: () => {
        const all = [...(successful || []), ...(failed || []), ...(pending || [])];
        if (all.length) downloadCSV(formatNotifications(all), 'channel_notifications');
      },
      xls: () => downloadXLS([], 'channel_notifications', {
        'Successful': formatNotifications(successful || []),
        'Failed': formatNotifications(failed || []),
        'Pending': formatNotifications(pending || [])
      }),
      json: () => downloadJSON({
        type: "channel_notifications",
        exported_at: new Date().toISOString(),
        successful: successful,
        failed: failed,
        pending: pending
      }, 'channel_notifications'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Channel Notifications</h1></div>
        <p>Successful: ${successful?.length || 0} | Failed: ${failed?.length || 0} | Pending: ${pending?.length || 0}</p>
        <h2>Failed Notifications</h2>
        <table>
          <tr><th>Time</th><th>Channel</th><th>Recipient</th><th>Error</th></tr>
          ${formatNotifications(failed || []).slice(0, 30).map(n => `
            <tr><td>${n.timestamp}</td><td>${n.channel}</td><td>${n.recipient}</td><td style="color:red">${n.error}</td></tr>
          `).join('')}
        </table>
      `, 'channel_notifications'),
      ai: () => downloadJSON({
        type: "channel_notifications",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total: (successful?.length || 0) + (failed?.length || 0) + (pending?.length || 0),
          successful: successful?.length || 0,
          failed: failed?.length || 0,
          pending: pending?.length || 0,
          success_rate: ((successful?.length || 0) / ((successful?.length || 0) + (failed?.length || 0)) * 100).toFixed(2) + '%',
          by_channel: {
            successful: successful?.reduce((acc, n) => { acc[n.channel] = (acc[n.channel] || 0) + 1; return acc; }, {}),
            failed: failed?.reduce((acc, n) => { acc[n.channel] = (acc[n.channel] || 0) + 1; return acc; }, {})
          }
        },
        data: { successful, failed, pending }
      }, 'ai_channel_notifications')
    };
  },

  /**
   * Export SOS Alerts
   */
  sosAlerts: (alerts: any[]) => {
    const formattedData = alerts.map(a => ({
      id: a.id,
      timestamp: a.timestamp || a.created_at,
      type: a.type,
      severity: a.severity,
      title: a.title,
      message: a.message,
      status: a.status,
      resolved_at: a.resolved_at,
      action_taken: a.action_taken || 'None'
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'sos_alerts'),
      xls: () => downloadXLS(formattedData, 'sos_alerts'),
      json: () => downloadJSON({
        type: "sos_alerts",
        exported_at: new Date().toISOString(),
        total_alerts: alerts.length,
        data: formattedData
      }, 'sos_alerts'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>SOS Alerts</h1></div>
        <p>Total Alerts: ${alerts.length}</p>
        <table>
          <tr><th>Time</th><th>Type</th><th>Severity</th><th>Status</th><th>Title</th></tr>
          ${formattedData.map(a => `
            <tr>
              <td>${a.timestamp}</td>
              <td>${a.type}</td>
              <td style="color: ${a.severity === 'critical' ? 'red' : a.severity === 'warning' ? 'orange' : 'blue'}">${a.severity}</td>
              <td>${a.status}</td>
              <td>${a.title}</td>
            </tr>
          `).join('')}
        </table>
      `, 'sos_alerts'),
      ai: () => downloadJSON({
        type: "sos_alerts",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_alerts: alerts.length,
          critical: alerts.filter(a => a.severity === 'critical').length,
          warnings: alerts.filter(a => a.severity === 'warning').length,
          info: alerts.filter(a => a.severity === 'info').length,
          resolved: alerts.filter(a => a.status === 'resolved').length,
          pending: alerts.filter(a => a.status === 'pending' || a.status === 'active').length
        },
        data: formattedData
      }, 'ai_sos_alerts')
    };
  },

  /**
   * Export Balance/Portfolio
   */
  balance: (data: any) => {
    const { balances, positions, summary } = data;
    
    const formatBalances = (balancesList: any[]) => balancesList?.map(b => ({
      asset: b.asset || b.currency,
      balance: b.balance,
      available: b.available,
      locked: b.locked || 0,
      usd_value: b.usd_value || b.value_in_usd,
      inr_value: b.inr_value || b.value_in_inr,
      updated_at: b.updated_at
    })) || [];
    
    const formatPositions = (positionsList: any[]) => positionsList?.map(p => ({
      symbol: p.symbol,
      exchange: p.exchange,
      quantity: p.quantity,
      avg_price: p.avg_price,
      current_price: p.current_price,
      pnl: p.pnl,
      pnl_percentage: p.pnl_percentage,
      status: p.status
    })) || [];
    
    return {
      csv: () => {
        if (balances?.length) downloadCSV(formatBalances(balances), 'account_balance');
        if (positions?.length) downloadCSV(formatPositions(positions), 'open_positions');
      },
      xls: () => downloadXLS([], 'account_balance', {
        'Balances': formatBalances(balances),
        'Positions': formatPositions(positions),
        'Summary': summary ? [summary] : []
      }),
      json: () => downloadJSON({
        type: "account_balance",
        exported_at: new Date().toISOString(),
        summary: summary,
        balances: balances,
        positions: positions
      }, 'account_balance'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Account Balance & Positions</h1></div>
        ${summary ? `
          <h2>Summary</h2>
          <p>Total Balance: ₹${summary.total_balance || 0}</p>
          <p>Available: ₹${summary.available_balance || 0}</p>
          <p>Open Positions: ${summary.open_positions || 0}</p>
          <p>Unrealized P&L: ₹${summary.unrealized_pnl || 0}</p>
        ` : ''}
        ${balances?.length ? `
          <h2>Balances</h2>
          <table>
            <tr><th>Asset</th><th>Balance</th><th>Available</th><th>INR Value</th></tr>
            ${formatBalances(balances).map(b => `
              <tr><td>${b.asset}</td><td>${b.balance}</td><td>${b.available}</td><td>₹${b.inr_value}</td></tr>
            `).join('')}
          </table>
        ` : ''}
      `, 'account_balance'),
      ai: () => downloadJSON({
        type: "account_balance",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: summary,
        balances: balances,
        positions: positions,
        analysis: {
          total_assets: balances?.length || 0,
          total_positions: positions?.length || 0,
          total_value_inr: summary?.total_balance || 0,
          total_unrealized_pnl: summary?.unrealized_pnl || 0
        }
      }, 'ai_account_balance')
    };
  }
};

// ============================================================================
// ADMIN EXPORTS
// ============================================================================

export const adminExports = {
  /**
   * Export User Management Data
   */
  userManagement: (users: any[]) => {
    const formattedData = users.map(u => ({
      user_id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      status: u.status,
      subscription_plan: u.subscription_plan,
      created_at: u.created_at,
      last_login: u.last_login,
      total_strategies: u.strategy_count || 0,
      total_trades: u.trade_count || 0
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'admin_users'),
      xls: () => downloadXLS(formattedData, 'admin_users'),
      json: () => downloadJSON({
        type: "admin_user_management",
        exported_at: new Date().toISOString(),
        total_users: users.length,
        data: formattedData
      }, 'admin_users'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>User Management Report</h1></div>
        <p>Total Users: ${users.length}</p>
        <table>
          <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Plan</th></tr>
          ${formattedData.slice(0, 50).map(u => `
            <tr><td>${u.name}</td><td>${u.email}</td><td>${u.role}</td><td>${u.status}</td><td>${u.subscription_plan}</td></tr>
          `).join('')}
        </table>
      `, 'admin_users'),
      ai: () => downloadJSON({
        type: "admin_user_management",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_users: users.length,
          active_users: users.filter(u => u.status === 'active').length,
          admin_users: users.filter(u => u.role === 'admin').length,
          by_plan: users.reduce((acc, u) => {
            acc[u.subscription_plan || 'none'] = (acc[u.subscription_plan || 'none'] || 0) + 1;
            return acc;
          }, {})
        },
        data: formattedData
      }, 'ai_admin_users')
    };
  },

  /**
   * Export Audit Logs
   */
  auditLogs: (logs: any[]) => {
    const formattedData = logs.map(l => ({
      timestamp: l.timestamp,
      user_id: l.user_id,
      user_name: l.user_name,
      action: l.action,
      resource: l.resource,
      details: l.details,
      ip_address: l.ip_address,
      status: l.status
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'audit_logs'),
      xls: () => downloadXLS(formattedData, 'audit_logs'),
      json: () => downloadJSON({
        type: "audit_logs",
        exported_at: new Date().toISOString(),
        total_records: logs.length,
        data: formattedData
      }, 'audit_logs'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Audit Logs</h1></div>
        <p>Total Records: ${logs.length}</p>
        <table>
          <tr><th>Timestamp</th><th>User</th><th>Action</th><th>Details</th></tr>
          ${formattedData.slice(0, 100).map(l => `
            <tr><td>${l.timestamp}</td><td>${l.user_name}</td><td>${l.action}</td><td>${l.details}</td></tr>
          `).join('')}
        </table>
      `, 'audit_logs'),
      ai: () => downloadJSON({
        type: "audit_logs",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_events: logs.length,
          unique_users: [...new Set(logs.map(l => l.user_id))].length,
          action_types: [...new Set(logs.map(l => l.action))]
        },
        data: formattedData
      }, 'ai_audit_logs')
    };
  },

  /**
   * Export Platform Reports
   */
  platformReports: (reports: any) => {
    const { overview, brokers, exchanges, channels, indicators, keywords, operators, themes, languages, features } = reports;
    
    return {
      csv: () => {
        // Export each category
        if (brokers?.all_brokers) downloadCSV(brokers.all_brokers, 'platform_brokers');
        if (exchanges?.all_exchanges) downloadCSV(exchanges.all_exchanges, 'platform_exchanges');
        if (channels?.channels) downloadCSV(channels.channels, 'platform_channels');
      },
      xls: () => downloadXLS([], 'platform_reports', {
        'Overview': overview ? [overview] : [],
        'Brokers': brokers?.all_brokers || [],
        'Indian Exchanges': exchanges?.indian_exchanges || [],
        'Crypto Exchanges': exchanges?.crypto_exchanges || [],
        'Channels': channels?.channels || [],
        'Indicators': indicators?.indicators || [],
        'Keywords': keywords?.keywords || [],
        'Operators': operators?.operators || [],
        'Themes': themes?.themes || [],
        'Languages': languages?.languages || [],
        'Features': features?.features || []
      }),
      json: () => downloadJSON({
        type: "platform_reports",
        version: "1.0",
        exported_at: new Date().toISOString(),
        reports: reports
      }, 'platform_reports'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Platform Reports Summary</h1></div>
        ${overview ? `
          <h2>Platform Overview</h2>
          <ul>
            <li>Brokers: ${overview.total_brokers}</li>
            <li>Indian Exchanges: ${overview.total_indian_exchanges}</li>
            <li>Crypto Exchanges: ${overview.total_crypto_exchanges}</li>
            <li>Indicators: ${overview.total_indicators}</li>
            <li>Channels: ${overview.total_channels}</li>
          </ul>
        ` : ''}
      `, 'platform_reports'),
      ai: () => downloadJSON({
        type: "platform_reports",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_brokers: overview?.total_brokers,
          total_indian_exchanges: overview?.total_indian_exchanges,
          total_crypto_exchanges: overview?.total_crypto_exchanges,
          total_indicators: overview?.total_indicators,
          total_keywords: overview?.total_keywords,
          total_channels: overview?.total_channels,
          total_operators: overview?.total_operators
        },
        reports: reports
      }, 'ai_platform_reports')
    };
  },

  /**
   * Export Analytics Data
   */
  analytics: (data: any) => {
    const { userAnalytics, revenueAnalytics, subscriptionAnalytics } = data;
    
    return {
      csv: () => {
        if (userAnalytics?.data) downloadCSV(userAnalytics.data, 'analytics_users');
        if (revenueAnalytics?.data) downloadCSV(revenueAnalytics.data, 'analytics_revenue');
      },
      xls: () => downloadXLS([], 'analytics', {
        'User Analytics': userAnalytics?.data || [],
        'Revenue Analytics': revenueAnalytics?.data || [],
        'Subscriptions': subscriptionAnalytics?.data || []
      }),
      json: () => downloadJSON({
        type: "analytics_data",
        version: "1.0",
        exported_at: new Date().toISOString(),
        ...data
      }, 'analytics'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Analytics Report</h1></div>
        <h2>Summary</h2>
        <pre>${JSON.stringify(data, null, 2)}</pre>
      `, 'analytics'),
      ai: () => downloadJSON({
        type: "analytics_data",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          period: data.period,
          total_users: userAnalytics?.total_users,
          active_users: userAnalytics?.active_users,
          revenue: revenueAnalytics?.total_revenue,
          subscriptions: subscriptionAnalytics?.total_subscriptions
        },
        data: data
      }, 'ai_analytics')
    };
  },

  /**
   * Export Transactions
   */
  transactions: (transactions: any[]) => {
    const formattedData = transactions.map(t => ({
      transaction_id: t.id,
      user: t.user_name,
      email: t.user_email,
      type: t.type,
      amount: t.amount,
      currency: t.currency || 'INR',
      status: t.status,
      payment_method: t.payment_method,
      created_at: t.created_at
    }));
    
    return {
      csv: () => downloadCSV(formattedData, 'transactions'),
      xls: () => downloadXLS(formattedData, 'transactions'),
      json: () => downloadJSON({
        type: "transactions",
        exported_at: new Date().toISOString(),
        total_transactions: transactions.length,
        total_amount: transactions.reduce((a, t) => a + t.amount, 0),
        data: formattedData
      }, 'transactions'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Transactions Report</h1></div>
        <p>Total: ${transactions.length} | Amount: ₹${transactions.reduce((a, t) => a + t.amount, 0).toLocaleString()}</p>
        <table>
          <tr><th>ID</th><th>User</th><th>Type</th><th>Amount</th><th>Status</th></tr>
          ${formattedData.slice(0, 50).map(t => `
            <tr><td>${t.transaction_id}</td><td>${t.user}</td><td>${t.type}</td><td>₹${t.amount}</td><td>${t.status}</td></tr>
          `).join('')}
        </table>
      `, 'transactions'),
      ai: () => downloadJSON({
        type: "transactions",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_transactions: transactions.length,
          total_amount: transactions.reduce((a, t) => a + t.amount, 0),
          successful: transactions.filter(t => t.status === 'success').length,
          failed: transactions.filter(t => t.status === 'failed').length
        },
        data: formattedData
      }, 'ai_transactions')
    };
  },

  /**
   * Export Admin Settings
   */
  adminSettings: (settings: any) => {
    return {
      csv: () => {
        const flatData = Object.entries(settings).map(([key, value]) => ({
          setting_key: key,
          value: typeof value === 'object' ? JSON.stringify(value) : value,
          type: typeof value
        }));
        downloadCSV(flatData, 'admin_settings');
      },
      xls: () => downloadXLS(
        Object.entries(settings).map(([key, value]) => ({ setting_key: key, value: typeof value === 'object' ? JSON.stringify(value) : value, type: typeof value })),
        'admin_settings'
      ),
      json: () => downloadJSON({
        type: "admin_settings",
        version: "1.0",
        exported_at: new Date().toISOString(),
        settings: settings
      }, 'admin_settings'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Admin Settings</h1></div>
        <pre>${JSON.stringify(settings, null, 2)}</pre>
      `, 'admin_settings'),
      ai: () => downloadJSON({
        type: "admin_settings",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total_settings: Object.keys(settings).length,
          categories: [...new Set(Object.keys(settings).map(k => k.split('.')[0]))]
        },
        settings: settings
      }, 'ai_admin_settings')
    };
  },

  /**
   * Export Admin Notifications
   */
  adminNotifications: (data: any) => {
    const { sent, failed, pending, by_channel, by_user } = data;
    
    const formatNotifications = (notifications: any[]) => notifications?.map(n => ({
      id: n.id,
      timestamp: n.timestamp || n.created_at,
      user_id: n.user_id,
      user_name: n.user_name,
      channel: n.channel,
      title: n.title,
      status: n.status,
      error: n.error || 'N/A',
      retry_count: n.retry_count || 0
    })) || [];
    
    return {
      csv: () => {
        const all = [...(sent || []), ...(failed || []), ...(pending || [])];
        if (all.length) downloadCSV(formatNotifications(all), 'admin_notifications');
      },
      xls: () => downloadXLS([], 'admin_notifications', {
        'Sent': formatNotifications(sent || []),
        'Failed': formatNotifications(failed || []),
        'Pending': formatNotifications(pending || [])
      }),
      json: () => downloadJSON({
        type: "admin_notifications",
        version: "1.0",
        exported_at: new Date().toISOString(),
        ...data
      }, 'admin_notifications'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Admin Notifications Report</h1></div>
        <p>Sent: ${sent?.length || 0} | Failed: ${failed?.length || 0} | Pending: ${pending?.length || 0}</p>
        <h2>Failed Notifications</h2>
        <table>
          <tr><th>Time</th><th>User</th><th>Channel</th><th>Error</th></tr>
          ${formatNotifications(failed || []).slice(0, 30).map(n => `
            <tr><td>${n.timestamp}</td><td>${n.user_name}</td><td>${n.channel}</td><td style="color:red">${n.error}</td></tr>
          `).join('')}
        </table>
      `, 'admin_notifications'),
      ai: () => downloadJSON({
        type: "admin_notifications",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: {
          total: (sent?.length || 0) + (failed?.length || 0) + (pending?.length || 0),
          sent: sent?.length || 0,
          failed: failed?.length || 0,
          pending: pending?.length || 0,
          success_rate: ((sent?.length || 0) / ((sent?.length || 0) + (failed?.length || 0)) * 100).toFixed(2) + '%',
          by_channel: by_channel,
          by_user: by_user
        },
        data: data
      }, 'ai_admin_notifications')
    };
  },

  /**
   * Export Admin Activities
   */
  adminActivities: (data: any) => {
    const { activities, summary } = data;
    
    const formatActivities = (activityList: any[]) => activityList?.map(a => ({
      id: a.id,
      timestamp: a.timestamp || a.created_at,
      user_id: a.user_id,
      user_name: a.user_name,
      action: a.action,
      resource: a.resource,
      details: a.details,
      ip_address: a.ip_address
    })) || [];
    
    return {
      csv: () => {
        if (activities?.length) downloadCSV(formatActivities(activities), 'admin_activities');
      },
      xls: () => downloadXLS([], 'admin_activities', {
        'Activities': formatActivities(activities),
        'Summary': summary ? [summary] : []
      }),
      json: () => downloadJSON({
        type: "admin_activities",
        version: "1.0",
        exported_at: new Date().toISOString(),
        ...data
      }, 'admin_activities'),
      pdf: () => downloadPDF(`
        <div class="header"><h1>Admin Activities Report</h1></div>
        <p>Total Activities: ${activities?.length || 0}</p>
        <table>
          <tr><th>Time</th><th>User</th><th>Action</th><th>Resource</th></tr>
          ${formatActivities(activities || []).slice(0, 50).map(a => `
            <tr><td>${a.timestamp}</td><td>${a.user_name}</td><td>${a.action}</td><td>${a.resource}</td></tr>
          `).join('')}
        </table>
      `, 'admin_activities'),
      ai: () => downloadJSON({
        type: "admin_activities",
        version: "1.0",
        exported_at: new Date().toISOString(),
        summary: summary,
        data: activities
      }, 'ai_admin_activities')
    };
  }
};

// ============================================================================
// EXPORT ALL FUNCTION
// ============================================================================

export const exportAll = {
  user: (userData: any) => ({
    csv: () => downloadJSON(userData, 'user_complete_data'),
    xls: () => downloadXLS([], 'user_complete_data', {
      'Profile': userData.profile ? [userData.profile] : [],
      'Activity': userData.activity || [],
      'Trades': userData.trades || [],
      'Strategies': userData.strategies || [],
      'Subscriptions': userData.subscriptions ? [userData.subscriptions] : []
    }),
    json: () => downloadJSON({
      type: "user_complete_export",
      version: "1.0",
      exported_at: new Date().toISOString(),
      ...userData
    }, 'user_complete_data'),
    pdf: () => downloadPDF(`
      <div class="header"><h1>User Complete Report</h1></div>
      <p>Generated: ${new Date().toLocaleString()}</p>
    `, 'user_complete_report'),
    ai: () => downloadJSON({
      type: "user_complete_export",
      version: "1.0",
      exported_at: new Date().toISOString(),
      ...userData
    }, 'ai_user_complete_data')
  }),
  
  admin: (adminData: any) => ({
    csv: () => downloadJSON(adminData, 'admin_complete_data'),
    xls: () => downloadXLS([], 'admin_complete_data', {
      'Users': adminData.users || [],
      'Audit Logs': adminData.auditLogs || [],
      'Transactions': adminData.transactions || [],
      'Analytics': adminData.analytics || []
    }),
    json: () => downloadJSON({
      type: "admin_complete_export",
      version: "1.0",
      exported_at: new Date().toISOString(),
      ...adminData
    }, 'admin_complete_data'),
    pdf: () => downloadPDF(`
      <div class="header"><h1>Admin Complete Report</h1></div>
      <p>Generated: ${new Date().toLocaleString()}</p>
    `, 'admin_complete_report'),
    ai: () => downloadJSON({
      type: "admin_complete_export",
      version: "1.0",
      exported_at: new Date().toISOString(),
      ...adminData
    }, 'ai_admin_complete_data')
  })
};

export default {
  convertToCSV,
  downloadCSV,
  downloadXLS,
  downloadJSON,
  downloadPDF,
  userExports,
  adminExports,
  exportAll
};
